/**
 * 统计列表 Campaign List
 */
import _ from 'lodash';
import moment from 'moment';
import * as types from 'src/store/types';
import request from 'src/utils/request';
import apis from 'src/const/apis';
import { isPerfTotalAsyncWhite, isPerfListCacheWhite, isRequiredCreativeWhite } from 'src/const/whiteList';
import {
  haveUVMetrics, filteredUVMetrics, mergeUVTotalData, handleUnSupportFilter,
} from 'src/utils/perfUtil';
import { getAccountTodayHMS } from 'src/utils/dateUtil';
import { captureError } from 'src/logger/errorLogger';
import { sendLog } from 'src/logger/customLogger';
import { jsonStringify } from 'src/utils/jsonFormat';
import { customContent } from 'src/const/state/custom-content';
import { RequiredTargets } from 'src/const/state/ad';

// campaign/ad/creative 列表进行拆分
const splitEnum = {
  // 主列表
  list: 1,
  // 汇总行
  total: 2,
};

// 短时切换 Tab 缓存时间
const cacheDuration = 2 * 60;

// state 初始化值写在这里，方便动态生成/清理
const getInitialState = () => ({
  batchCustomContent: {
    columnsTemplate: [],
    filterTemplate: [],
    listQueryConfig: [],
  },
  campaign: {
    loading: false,
    listData: {},
    requestStr: '',
    timestamp: '',
    token: '',
    lastUpdateTime: '',
    total: {
      loading: false,
      error: false,
    },
  },
  ad: {
    loading: false,
    listData: {},
    requestStr: '',
    timestamp: '',
    token: '',
    lastUpdateTime: '',
    total: {
      loading: false,
      error: false,
    },
  },
  creative: {
    loading: false,
    listData: {},
    requestStr: '',
    timestamp: '',
    token: '',
    lastUpdateTime: '',
    total: {
      loading: false,
      error: false,
    },
  },
  videoSourceList: {},
  haveOperated: false,
});

export default {
  namespaced: true,
  state: getInitialState(),
  getters: {
    columnsTemplate: state => state.batchCustomContent.columnsTemplate || [],
    filterTemplate: state => state.batchCustomContent.filterTemplate || [],
    listQueryConfig: state => state.batchCustomContent.listQueryConfig || [],
    lastUpdateTimeMap: state => ({
      campaign: state.campaign.lastUpdateTime,
      ad: state.ad.lastUpdateTime,
      creative: state.creative.lastUpdateTime,
    }),
    statisticsData: state => ({
      campaign: state.campaign.listData.statistics,
      ad: state.ad.listData.statistics,
      creative: state.creative.listData.statistics,
    }),
    isTotalLoading: state => ({
      campaign: state.campaign?.total?.loading,
      ad: state.ad?.total?.loading,
      creative: state.creative?.total?.loading,
    }),
  },
  mutations: {
    [types.SET_REQUEST_TOKEN](state, { key, token }) {
      if (state[key]) {
        state[key].token = token;
      }
    },
    [types.SET_PERF_LIST](state, { key, listObj = {} }) {
      state[key] = listObj;
    },
    [types.SET_BATCH_CUSTOM_CONTENT](state, { listObj = {} }) {
      state.batchCustomContent = listObj;
    },
    [types.SET_BATCH_CUSTOM_CONTENT_BY_KEY](state, { key, listObj = [] }) {
      (state.batchCustomContent)[key] = listObj;
    },
    [types.SET_SOURCE_LIST](state, { key, listObj = {} }) {
      state[key] = listObj;
    },
    [types.SET_PERF_OPERATED_ANCHOR](state) {
      state.haveOperated = true;
    },
  },
  actions: {
    async getPerfList({
      commit, state, getters, dispatch,
    }, { level, params }) {
      let requestErr = null;
      // 重复请求时清除之前的请求
      if (state[level]?.token) {
        state[level].token.cancel();
      }
      const cancelToken = request.axios.CancelToken;
      const source = cancelToken.source();
      commit(types.SET_REQUEST_TOKEN, {
        key: level,
        token: source,
      });
      let data = {};
      // 开白533且只有creative层级访问新接口，要带上全量的投前指标
      if (isRequiredCreativeWhite() && level === 'creative') {
        params.query_list = params.query_list.concat(RequiredTargets);
        params.is_attr_required = 1;
      }
      const requestParams = {
        ...params,
      };
      let fetchTotalAsync = false;
      const timestamp = Date.now();
      try {
        // 判断是否包含 白名单 / UV 类指标
        fetchTotalAsync = isPerfTotalAsyncWhite() && haveUVMetrics(params?.query_list || []);
        if (fetchTotalAsync) {
          // 更新时间戳、重置列表数据
          await dispatch('resetListDataWithTimestamp', { level, timestamp });
          dispatch('getTotalData', {
            level, params, timestamp, source,
          });
          requestParams.split = [splitEnum.list];
        }
        const resp = await request.post(apis[level].newStatisticList, requestParams, {
          cancelToken: source.token,
        });
        data = resp.data || {};
        const listData = data;
        const { listData: mergedData = {} } = state[level] || {};
        // 非拆分获取、拆分获取且汇总行未返回 则直接用列表数据
        // 拆分获取且汇总行已返回 需要合并汇总行数据
        if (fetchTotalAsync && !getters.isTotalLoading?.[level]) {
          listData.statistics = {
            ...data.statistics,
            ...mergeUVTotalData({
              total: mergedData.statistics,
              data: data.statistics,
            }),
          };
        }
        commit(types.SET_PERF_LIST, {
          key: level,
          listObj: {
            timestamp,
            requestStr: Object.freeze(params),
            loading: false,
            listData: Object.freeze(listData),
            token: source,
            lastUpdateTime: getAccountTodayHMS(),
            total: state[level]?.total,
          },
        });
        // 每次获取列表数据后要清空上一次getSourceList获取的videoSourceList
        commit(types.SET_SOURCE_LIST, {
          key: 'videoSourceList',
          listObj: {},
        });
      } catch (err) {
        requestErr = err;
        captureError(err);
      }
      if (fetchTotalAsync) {
        dispatch('finishAsyncRequest', { level, params, timestamp });
      }
      return { data, err: requestErr || {} };
    },
    async checkCacheAndGetList({ state, dispatch }, { level, params }) {
      return new Promise(async (resolve) => {
        const { listData = {}, timestamp, requestStr } = state[level] || {};
        const diff = moment().diff(moment(timestamp), 'seconds');
        if (!state.haveOperated && isPerfListCacheWhite() && _.isEqual(params, requestStr)
          && diff < cacheDuration && listData?.table?.length) {
          setTimeout(() => resolve({ data: listData }));
          sendLog('counter', {
            name: 'list_cache_hit',
            value: 1,
            tags: {
              level,
              diff: String(diff),
            },
          });
          return;
        }
        const res = await dispatch('getPerfList', { level, params });
        resolve(res);
      });
    },

    // 按需获取当前列表页所有aco创意的source字段
    async getSourceList({
      commit,
    }, { level, params }) {
      let data = {};
      try {
        const resp = await request.post(apis[level].requiredStatisticList, params);
        data = resp.data || {};
        // 以对象形式存储vid对应的source值
        const videoSourceList = data.video_info.map(val => ({
          vid: val?.vid,
          source: val?.source || 0,
        })).reduce((acc, cur) => ({ ...acc, [cur.vid]: cur.source }), {});
        commit(types.SET_SOURCE_LIST, {
          key: 'videoSourceList',
          listObj: videoSourceList,
        });
        return data;
      } catch (err) {
        commit(types.SET_SOURCE_LIST, {
          key: 'videoSourceList',
          listObj: {},
        });
        captureError(err);
      }
    },
    // 点击创意预览视频后按需获取vid对应的download_allowed字段
    async getDownloadAllowed({
      commit,
    }, { level, params }) {
      let videoInfo = {};
      try {
        const resp = await request.post(apis[level].requiredStatisticList, params);
        const { code, data = {} } = resp;
        if (data && code === 0) {
          videoInfo = data?.video_info[0] || {};
        }
        return videoInfo;
      } catch (err) {
        captureError(err);
      }
    },
    async getTotalData({ commit, state, dispatch }, {
      level, params, timestamp, source,
    }) {
      //  UV 类指标单独请求汇总行数据
      let data = {};
      const { timestamp: requestStamp } = state[level] || {};
      try {
        const resp = await request.post(apis[level].newStatisticList, {
          ...params,
          query_list: filteredUVMetrics(params.query_list),
          split: [splitEnum.total],
        }, { cancelToken: source?.token });
        data = resp.data || {};
        // 用时间戳判断为同一次列表请求，避免数据覆盖
        if (requestStamp === timestamp) {
          const statistics = data?.statistics || {};
          const { listData = {} } = state[level] || {};
          const mergedData = {
            ...state[level],
            listData: {
              ...listData,
              statistics: mergeUVTotalData({
                total: statistics,
                data: listData.statistics,
              }),
            },
            total: {
              loading: false,
              error: false,
            },
          };
          commit(types.SET_PERF_LIST, {
            key: level,
            listObj: mergedData,
          });
        }
      } catch (err) {
        captureError(err);
        const isTotalError = state[level]?.total?.error;
        // 汇总行请求出错
        commit(types.SET_PERF_LIST, {
          key: level,
          listObj: {
            ...state[level],
            total: {
              error: true,
              // isTotalError 为 true 即当前为二次重试，依然报错
              loading: !isTotalError,
            },
          },
        });
        if (isTotalError) {
          // 监控上报
          sendLog('counter', {
            name: 'total_request_error',
            value: 1,
            tags: {
              level,
            },
          });
          sendLog('log', {
            level: 'error',
            value: `total request error: ${level} ${jsonStringify(params)}`,
            tags: {
              level,
            },
          });
        }
      }
      dispatch('finishAsyncRequest', { level, params, timestamp });
      return data;
    },
    async resetListDataWithTimestamp({ commit, state }, { level, timestamp }) {
      const mergedData = {
        ...state[level],
        timestamp,
        listData: {},
        loading: true,
        total: {
          loading: true,
          error: false,
        },
      };
      commit(types.SET_PERF_LIST, {
        key: level,
        listObj: mergedData,
      });
    },
    async finishAsyncRequest({ state, dispatch }, { level, params, timestamp }) {
      const { loading, total = {} } = state[level] || {};
      // 拆分请求都完成了
      if (!loading && !total.loading && total.error) {
        dispatch('retryTotalRequest', { level, params, timestamp });
      }
    },
    // 针对主列表拉取成功但汇总行失败的情况进行一次重试
    async retryTotalRequest({ state, dispatch }, { level, params, timestamp }) {
      try {
        const { timestamp: requestStamp } = state[level] || {};
        if (requestStamp === timestamp) {
          dispatch('getTotalData', {
            level,
            timestamp,
            params: {
              ...params,
              [`${level}_ids`]: state[level]?.table?.map(item => item[`${level}_id`]) || [],
            },
          });
        }
      } catch (err) {
        captureError(err);
      }
    },
    // 获取自定义列列表 参数type类型为Array 可传递多个type参数  type_list: [1,2,3]
    async getBatchCustomContentList({ commit, state }, data) {
      const resp = await request.post(apis.custom_content.newList, data);
      const customDataList = resp?.data?.list || [];
      const columnsTemplateData = customDataList.filter(item => item.type === customContent.BIDDING_CUSMTOM_COLUMN);
      const filterTemplateData = customDataList.filter(item => item.type === customContent.BIDDING_FILTER);
      const listQueryConfigData = customDataList.filter(item => item.type === customContent.BIDDING_STATISTIC_USER_CONFIG);

      commit(types.SET_BATCH_CUSTOM_CONTENT, {
        listObj: {
          columnsTemplate: columnsTemplateData,
          filterTemplate: filterTemplateData.map(item => ({
            id: item.id,
            label: item.name,
            value: handleUnSupportFilter(item.data),
            canDelete: true,
          })),
          listQueryConfig: listQueryConfigData.map(item => ({
            ...item,
            data: handleUnSupportFilter(item.data),
          })),
        },
      });
    },
    async setPerfOperatedAnchor({ commit }) {
      commit(types.SET_PERF_OPERATED_ANCHOR);
    },
  },
};
